import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Fuso único do produto (decisão da feature de agendamento). As datas dos slots
 * chegam em ISO UTC; os rótulos são exibidos na hora local do prestador.
 */
export const SCHEDULE_TZ = 'America/Sao_Paulo';

/**
 * Offset fixo do fuso do produto. O Brasil não observa horário de verão desde
 * 2019 e a agenda usa fuso único (America/Sao_Paulo), então -03:00 é constante.
 */
export const SCHEDULE_UTC_OFFSET = '-03:00';

/** Converte data local (YYYY-MM-DD) + hora (HH:mm) do fuso da agenda em ISO UTC. */
export function zonedLocalToIso(dateKey: string, time: string): string {
  return new Date(`${dateKey}T${time}:00${SCHEDULE_UTC_OFFSET}`).toISOString();
}

/** 'HH:mm' de um instante ISO, no fuso da agenda. */
export function formatSlotTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: SCHEDULE_TZ,
  });
}

/** 'HH:mm–HH:mm'. */
export function formatSlotRange(startIso: string, endIso: string): string {
  return `${formatSlotTime(startIso)}–${formatSlotTime(endIso)}`;
}

/** Rótulo curto de um dia local (YYYY-MM-DD): 'ter, 12 jul'. */
export function formatDayLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return format(date, "EEE, dd MMM", { locale: ptBR }).toLowerCase();
}

/** Partes de um dia local (YYYY-MM-DD) para o seletor de dias. */
export function dayParts(dateStr: string): {
  weekday: string;
  day: string;
  month: string;
} {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return {
    weekday: format(date, 'EEE', { locale: ptBR }).replace('.', ''),
    day: format(date, 'dd'),
    month: format(date, 'MMM', { locale: ptBR }).replace('.', ''),
  };
}

/** 'ter, 12 jul • 10:00' a partir de um instante ISO. */
export function formatAppointmentWhen(iso: string): string {
  const datePart = new Date(iso).toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    timeZone: SCHEDULE_TZ,
  });
  return `${datePart.replace('.', '')} • ${formatSlotTime(iso)}`;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

/** Dia da semana (0=dom … 6=sáb) de um instante ISO, no fuso da agenda. */
export function zonedWeekday(iso: string): number {
  const label = new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short',
    timeZone: SCHEDULE_TZ,
  });
  return WEEKDAY_INDEX[label] ?? 0;
}

/** Minutos desde a meia-noite (0–1439) de um instante ISO, no fuso da agenda. */
export function zonedMinutesOfDay(iso: string): number {
  const [h, m] = new Date(iso)
    .toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: SCHEDULE_TZ,
    })
    .split(':')
    .map(Number);
  return h * 60 + m;
}

/** 'YYYY-MM-DD' local (fuso da agenda) de um instante ISO. */
export function zonedDateKey(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: SCHEDULE_TZ });
}

/** 'HH:mm' → minutos desde a meia-noite. */
export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
}

/** Rótulo longo de um dia local (YYYY-MM-DD): 'terça-feira, 12 de julho'. */
export function formatDayLabelLong(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return format(date, "EEEE, dd 'de' MMMM", { locale: ptBR });
}

/** 'setembro de 2026' a partir de qualquer dia 'YYYY-MM-DD' do mês. */
export function formatMonthLabel(dateStr: string): string {
  const [y, m] = dateStr.split('-').map(Number);
  return format(new Date(y, m - 1, 1), "MMMM 'de' yyyy", { locale: ptBR });
}

/** Duração legível: 30min, 1h, 1h30. */
export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
}
